import React, { useEffect, useRef } from 'react';
import { MechPoint, MTH } from './mechShared';

// ═══════════════════════════════════════════════
// 通用深度剖面 canvas — 左深度轴 + 多个横向曲线道
// profile / stress 两个页面共用
// ═══════════════════════════════════════════════
export type Series<P = MechPoint> = {
  label: string;
  color: string;
  accessor: (p: P) => number | null;
};
export type Track<P = MechPoint> = {
  title: string;
  unit: string;
  min: number;
  max: number;
  series: Series<P>[];
};

export default function DepthProfile<P extends { depth: number }>({
  points,
  tracks,
}: {
  points: P[];
  tracks: Track<P>[];
}) {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d')!;
    const dpr = window.devicePixelRatio || 2;
    const rect = cv.getBoundingClientRect();
    cv.width = rect.width * dpr; cv.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    ctx.fillStyle = MTH.canvasBg; ctx.fillRect(0, 0, W, H);
    if (!points.length) return;

    const depths = points.map((p) => p.depth);
    const dMin = Math.min(...depths), dMax = Math.max(...depths);
    const padT = 46, padB = 24, padL = 54;
    const plotH = H - padT - padB;
    const yAt = (d: number) => padT + ((d - dMin) / (dMax - dMin || 1)) * plotH;

    // 深度轴
    ctx.strokeStyle = MTH.headerBorder; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(padL, padT); ctx.lineTo(padL, padT + plotH); ctx.stroke();
    ctx.fillStyle = MTH.textMuted; ctx.font = `10px ${MTH.fontMono}`; ctx.textAlign = 'right';
    const dStep = (dMax - dMin) > 400 ? 100 : (dMax - dMin) > 150 ? 50 : 20;
    for (let d = Math.ceil(dMin / dStep) * dStep; d <= dMax; d += dStep) {
      const y = yAt(d);
      ctx.strokeStyle = MTH.grid; ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(W - 10, y); ctx.stroke();
      ctx.fillStyle = MTH.textMuted; ctx.fillText(`${d}`, padL - 5, y + 3);
    }
    ctx.save(); ctx.translate(12, padT + plotH / 2); ctx.rotate(-Math.PI / 2);
    ctx.textAlign = 'center'; ctx.fillStyle = MTH.textSec; ctx.font = `11px ${MTH.fontUI}`;
    ctx.fillText('深度 (m)', 0, 0); ctx.restore();

    // 道
    const n = tracks.length;
    const gap = 14;
    const trackW = (W - padL - 16 - gap * (n - 1)) / n;
    ctx.textAlign = 'left';
    tracks.forEach((tr, ti) => {
      const x0 = padL + ti * (trackW + gap);
      const x1 = x0 + trackW;
      // 道边框
      ctx.strokeStyle = MTH.grid; ctx.lineWidth = 1;
      ctx.strokeRect(x0, padT, trackW, plotH);
      const xAt = (v: number) => x0 + ((v - tr.min) / (tr.max - tr.min || 1)) * trackW;

      // 标题 + 范围
      ctx.fillStyle = MTH.text; ctx.font = `bold 11px ${MTH.fontUI}`; ctx.textAlign = 'center';
      ctx.fillText(tr.title, x0 + trackW / 2, padT - 28);
      ctx.fillStyle = MTH.textMuted; ctx.font = `9px ${MTH.fontMono}`;
      ctx.fillText(`${tr.min}~${tr.max} ${tr.unit}`, x0 + trackW / 2, padT - 16);

      // 每条 series
      tr.series.forEach((s, si) => {
        ctx.strokeStyle = s.color; ctx.lineWidth = 1.6; ctx.beginPath();
        let started = false;
        points.forEach((p) => {
          const v = s.accessor(p);
          if (v == null || isNaN(v)) return;
          const x = Math.max(x0, Math.min(x1, xAt(v)));
          const y = yAt(p.depth);
          if (!started) { ctx.moveTo(x, y); started = true; } else ctx.lineTo(x, y);
        });
        ctx.stroke();
        // 图例(多 series 时画在道顶)
        if (tr.series.length > 1) {
          const ly = padT - 4;
          const lx = x0 + 6 + si * (trackW / tr.series.length);
          ctx.strokeStyle = s.color; ctx.lineWidth = 2;
          ctx.beginPath(); ctx.moveTo(lx, ly); ctx.lineTo(lx + 12, ly); ctx.stroke();
          ctx.fillStyle = MTH.textSec; ctx.font = `9px ${MTH.fontUI}`; ctx.textAlign = 'left';
          ctx.fillText(s.label, lx + 15, ly + 3);
        }
      });
    });
  }, [points, tracks]);

  return <canvas ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />;
}
